import { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import UserContext from "../context/user-context";
import NewEvent from "../passapp-sdk/newEvent";
import User from "../passapp-sdk/user";
import {
  showAlertInAction,
  showAlertOutAction,
  showAlertRejectAction,
} from "../redux/actions/alertAction";
import { AuthorizationType, GuestType } from "../store/types/userTypes";
import { useStore } from "./useStore";
import { useTranslation } from "./useTranslation";
import {UserState} from "../passapp-sdk/types/userTypes";
import {setOpenedVehicleSection} from "../redux/actions/registerExitActions";

const useActionEvent = () => {
  const { state } = useContext(UserContext);
  const { authStore } = useStore();
  const dispatch = useDispatch();
  const { t, BundleKey } = useTranslation();
  const userReq = new User();
  const newEvent = new NewEvent();
  const [loadingApprove, setLoadingApprove] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);
  const { registerExitStore } = useStore();

  const getErrorsFields = (): string[] | null => {
    let errors: string[] = [];
    if (
      !state.firstname ||
      !state.lastname ||
      !state.dni ||
      !state.countryCode
    ) {
      errors.push(t(BundleKey.ERROR_PERSONALINF_MUST));
    }
    if (!state.authorizations.some((item) => item.mark)) {
      errors.push(t(BundleKey.ERROR_PASS_MUST));
    }
    if (!state.vehicleType && !state.config?.companion) {
      errors.push(t(BundleKey.ERROR_METHOD_MUST));
    }
    if (state.vehicleType === "car") {
      if (!state.vehicleSelected || !state.vehicleSelected.plate)
        errors.push(t(BundleKey.ERROR_VEHICLE_SELECT));
    }
    if (
      state.authorizations.some(
        (item) => item.mark && item.guestType === GuestType.provider
      )
    ) {
      if (state.professionTypes.length === 0)
        errors.push(t(BundleKey.ERROR_PROVIDER_MUST));
    }
    if (
      state.isInvalidArtExpirationDate ||
      state.isInvalidCriminalRecordIssueDate ||
      state.isInvalidDriverLicenseExpirationDate ||
      state.isInvalidInsuranceExpirationDate
    )
      errors.push(t(BundleKey.ERROR_DATE_INVALID_MUST));
    if (errors.length === 0) return null;
    return errors;
  };

  const isValid = (): boolean => {
    return getErrorsFields() === null;
  };

  const loading = (submit: boolean, value: boolean) => {
    if (submit) setLoadingApprove(value);
    else setLoadingReject(value);
  };

  const actionEvent = (userEvent: any, toSuccess?: () => void) => {
    loading(userEvent.type !== "reject", true);
    newEvent
      .addEventAll(userEvent)
      .then((response) => {
        if (response.data.type === "in") showAlertInAction(dispatch);
        if (response.data.type === "reject") showAlertRejectAction(dispatch);
        if (response.data.type === "out") showAlertOutAction(dispatch);
        if (toSuccess) toSuccess();
      })
      .catch((error) => {
        console.warn("ERROR", error);
      })
      .finally(() => {
        loading(userEvent.type !== "reject", false);
      });
  };

  const sendIndividualEvent = async (
    user: UserState,
    eventType: "in" | "out" | "reject",
    toSuccess?: () => void
  ) => {
    let userEvent: any = {
      token: authStore.token,
      type: eventType,
      who: user.id,
      eventId: user.eventId,
      comment: user.comment,
      underagedCount: user.underagedCount || "0",
      vehicleType: user.vehicleType,
      eventSource: "web",
    };
    userEvent = verifyAuthorization(userEvent, user);
    userEvent = verifyBlackList(userEvent, user);
    userEvent = verifyVehicle(userEvent, user);
    userEvent = verifyVehicleOverride(userEvent, user);
    userEvent = await verifyWho(userEvent, user);
    actionEvent(userEvent, toSuccess);
  };

  const sendEvent = async (toSuccess?: () => void, reject?: boolean) => {
    if (isValid() || reject) {
      let userEvent: any = {
        token: authStore.token,
        type: reject ? "reject" : state.config?.eventType || "in",
        who: state.id,
        eventId: state.eventId,
        comment: reject ? state.commentReject : state.comment,
        peopleComment: state.permanentComment ?? '',
        underagedCount: state.underagedCount || "0",
        vehicleType: state.vehicleType,
        eventSource: "web",
      };
      userEvent = verifyAuthorization(userEvent, state);
      userEvent = verifyBlackList(userEvent, state);
      userEvent = verifyVehicle(userEvent, state);
      if (registerExitStore.openedVehicleSection) {
        userEvent = verifyVehicleOverride(userEvent, state);
        dispatch(setOpenedVehicleSection(false));
      }
      userEvent = await verifyWho(userEvent, state);

      if (state.companions?.length > 0) {
        Promise.all(
          state.companions?.map(async (item) => {
            let companionEvent = {
              token: authStore.token,
              type: reject ? "reject" : state.config?.eventType || "in",
              who: item.id,
              eventId: item.eventId,
              comment: reject ? state.commentReject : item.comment,
              underagedCount: item.underagedCount || "0",
              eventSource: "web",
              mainEvent: state.eventId,
              vehicleType: state.vehicleType,
              driver: state.id,
            };
            companionEvent = verifyVehicle(companionEvent, state);
            companionEvent = verifyAuthorization(companionEvent, item);
            companionEvent = verifyBlackList(companionEvent, item);
            companionEvent = await verifyWho(companionEvent, item);
            actionEvent(companionEvent, toSuccess);
          })
        );
      }
      actionEvent(userEvent, toSuccess);
    }
  };

  const verifyAuthorization = (event: any, state: UserState) => {
    let invites: string[] = [];
    let hosts: string[] = [];
    let hostConfirmations: string[] = [];
    let authorizations: string[] = [];
    let entriesAsProvider: string[] = [];
    let lots: string[] = [];
    const filterLots = (id: string) => {
      if (lots.find((item) => item === id) === undefined)
        lots = lots.concat(id);
    };
    state.authorizations.map((passes) => {
      if (passes.mark) {
        if (
          passes.authorization.type === "invite" &&
          passes.type !== AuthorizationType.lastEntrance
        ) {
          invites.push(passes.id);
        }
        if (
          passes.authorization.type === "authorization" &&
          passes.type !== AuthorizationType.lastEntrance
        ) {
          if (passes.guestType === GuestType.provider)
            authorizations.push(passes.id);
          else hosts.push(passes.authorization.host.id);
        }
        if (
          passes.authorization.type === "resident" ||
          passes.type === AuthorizationType.lastEntrance
        ) {
          if (passes.authorization.host.customLot)
            filterLots(passes.authorization.host.id);
          else hostConfirmations.push(passes.authorization.host.id);
        }
        if (
          passes.authorization.type === "request" &&
          passes.type !== AuthorizationType.lastEntrance
        ) {
          invites.push(passes.id);
        }
        if (passes.type === AuthorizationType.authorizedOnGuard) {
          if (passes.authorization.host.customLot)
            filterLots(passes.authorization.host.id);
          else hostConfirmations.push(passes.id);
        }
        if (passes.authorization.host) {
          if (passes.guestType === GuestType.provider) {
            entriesAsProvider.push(passes.authorization.host.id);
          }
        }
      }
    });
    return ({
      ...event,
      invites,
      hosts,
      lots,
      hostConfirmations,
      authorizations,
      entriesAsProvider,
    });
  };

  const verifyBlackList = (event: any, state: UserState): any => {
    if (state.blacklistAuthorization) {
      event = {
        ...event,
        blacklistAuthorization: state.blacklistAuthorization,
      };
    }
    return event;
  };

  const verifyVehicleOverride = (event: any, state: UserState) => {
    if (state.vehicleType === "car") {
      const oldVehicle = state.vehicles?.find(
        (vehicle) => vehicle.id === state.vehicleSelected.id
      );
      if (!oldVehicle) return event;
      if (
        oldVehicle.brand !== state.vehicleSelected.brand ||
        oldVehicle.plate !== state.vehicleSelected.plate ||
        oldVehicle.model !== state.vehicleSelected.model ||
        oldVehicle.color !== state.vehicleSelected.color ||
        oldVehicle.insuranceExpirationDate !==
          state.vehicleSelected.insuranceExpirationDate
      ) {
        event = {
          ...event,
          vehicleOverrides: {
            id: oldVehicle.id,
            plate: state.vehicleSelected.plate,
            brand: state.vehicleSelected.brand,
            model: state.vehicleSelected.model,
            color: state.vehicleSelected.color,
            insuranceExpirationDate:
              state.vehicleSelected.insuranceExpirationDate,
          },
        };
      }
    }
    return event;
  };

  const verifyVehicle = (event: any, state: UserState) => {
    if (state.vehicleType === "car" && state.vehicleSelected.plate) {
      event = {
        ...event,
        // `selectVehicle` devuelve el id del vehículo ya sincronizado en el complejo. Preferirlo
        // evita que el evento vuelva a resolver una patente contra la antigua lista local. La
        // patente queda como compatibilidad para vehículos heredados sin id.
        ...(state.vehicleSelected.id
          ? { vehicleId: state.vehicleSelected.id }
          : { vehicle: state.vehicleSelected.plate }),
      };
    }
    return event;
  };

  const verifyWho = async (event: any, state: UserState) => {
    if (
      state.firstname !== state.old.firstname ||
      state.lastname !== state.old.lastname ||
      state.dni !== state.old.dni ||
      state.driverLicenseExpirationDate !==
        state.old.driverLicenseExpirationDate ||
      state.professionTypes?.toString() !==
        state.old.professionTypes?.toString() ||
      state.workMaterials !== state.old.workMaterials ||
      state.artCompanyName !== state.old.artCompanyName ||
      state.artPolicyNumber !== state.old.artPolicyNumber ||
      state.artExpirationDate !== state.old.artExpirationDate ||
      state.criminalRecordIssueDate !== state.old.criminalRecordIssueDate
    ) {
      let whoOver: any = {};
      if (state.firstname !== state.old.firstname) {
        whoOver = { firstname: state.firstname };
      }
      if (state.lastname !== state.old.lastname) {
        whoOver = { ...whoOver, lastname: state.lastname };
      }
      if (
        state.driverLicenseExpirationDate &&
        state.driverLicenseExpirationDate !==
          state.old.driverLicenseExpirationDate
      ) {
        whoOver = {
          ...whoOver,
          driverLicenseExpirationDate: state.driverLicenseExpirationDate,
        };
      }
      if (
        state.professionTypes &&
        state.professionTypes?.toString() !==
          state.old.professionTypes.toString()
      ) {
        whoOver = {
          ...whoOver,
          professionTypes: state.professionTypes,
        };
      }
      if (
        state.workMaterials &&
        state.workMaterials !== state.old.workMaterials
      ) {
        whoOver = {
          ...whoOver,
          workMaterials: state.workMaterials,
        };
      }

      if (
        state.artCompanyName &&
        state.artCompanyName !== state.old.artCompanyName
      ) {
        whoOver = {
          ...whoOver,
          artCompanyName: state.artCompanyName,
        };
      }

      if (
        state.artPolicyNumber &&
        state.artPolicyNumber !== state.old.artPolicyNumber
      ) {
        whoOver = {
          ...whoOver,
          artPolicyNumber: state.artPolicyNumber,
        };
      }

      if (
        state.artExpirationDate &&
        state.artExpirationDate !== state.old.artExpirationDate
      ) {
        whoOver = {
          ...whoOver,
          artExpirationDate: state.artExpirationDate,
        };
      }

      if (
        state.criminalRecordIssueDate &&
        state.criminalRecordIssueDate !== state.old.criminalRecordIssueDate
      ) {
        whoOver = {
          ...whoOver,
          criminalRecordIssueDate: state.criminalRecordIssueDate,
        };
      }
      whoOver = { ...whoOver, dni: state.dni };
      if (Object.values(whoOver).length > 1) {
        event = {
          ...event,
          whoOverrides: whoOver,
        };
      }
      if (state.registrationType !== "full")
        await userReq.updateUser(authStore.token, event.who, whoOver);
    }
    return event;
  };

  return {
    getErrorsFields,
    sendEvent,
    sendIndividualEvent,
    loadingApprove,
    loadingReject,
  };
};

export default useActionEvent;
